import { Component, signal, Signal } from '@angular/core';
import { toSignal } from "@angular/core/rxjs-interop";
import { interval, map } from "rxjs";

import { UiLibraryModule } from "../../../../template-ui.module";
import { CommonModule } from "@angular/common";
import { DocMeta } from "../../../../decorators";
import { ButtonConfig } from "@template-ui/types";

@DocMeta({
  hide: true,
  showcaseTitle: "Explore Reactive Inputs",
  showcaseDesc:`
  This sample explores the use of ReactiveInputs to help bind config properties to component properties. With Reactive Inputs,
  a parent component can read/change the properties of a config object directly without the need to get a reference to
  the actual component.
  `

})
@Component({
    selector: 'ui-explore-reactive-inputs, ez-explore-reactive-inputs',
    imports: [CommonModule, UiLibraryModule],
    templateUrl: './explore-reactive-inputs.component.html',
    styleUrl: './explore-reactive-inputs.component.scss'
})
export class ExploreReactiveInputsComponent {
   isDisabled = false;

    isDisabledForFunction: boolean = false;
    getIsDisabled(): boolean
    {
      return this.isDisabled;
    }

    isDisabledSig = signal(false);

    // demo-snippets.component.ts

    isDisabled$ = interval(3000).pipe(map(i => i % 2 === 0)); // toggles every 3s

    configHtmlSnippet5 =
  `<ui-button [config]="config5"></ui-button>`;



    nativeObservableButtonSnippet =
  `<button [disabled]="(isDisabled$ | async)">
    Button
  </button>`;

  configObservableButtonSnippet =
  `<ui-button [disabled]="(isDisabled$ | async)">
    ConfigButton
  </ui-button>`;


    nativeConstantButtonSnippet =
  `<button [disabled]="true">
    Button
  </button>`;

    configConstantButtonSnippet =
  `<ui-button [disabled]="true">
    ConfigButton
  </ui-button>`;

    nativeStaticButtonSnippet =
  `<button [disabled]="isDisabled">
    Button
  </button>`;

    configStaticButtonSnippet =
  `<ui-button [disabled]="isDisabled">
    ConfigButton
  </ui-button>`;

    nativeFunctionButtonSnippet =
  `<button [disabled]="getIsDisabled()">
    Button
  </button>`;

    configFunctionButtonSnippet = `
  <ui-button [disabled]="getIsDisabled()">
    ConfigButton
  </ui-button>`;

    nativeSignalButtonSnippet1 =
  `<button [disabled]="isDisabledSig()">
    Button
  </button>`;

    nativeSignalButtonSnippet2 =
  `<button [disabled]="isDisabledSig">
    Button
  </button>`;

    configSignalButtonSnippet1 =
  `<ui-button [disabled]="isDisabledSig">
    ConfigButton ✅
  </ui-button>`;

    configSignalButtonSnippet2 =
  `<ui-button [disabled]="isDisabledSig()">
    ConfigButton () ❌
  </ui-button>`;

    // Constant
    configHtmlSnippet1 = `<ui-button [config]="config1">Config 1</ui-button>`;
    configTsSnippet1 = `config1 = { disabled: true };`;

    // Static Variable
    configHtmlSnippet2 = `<ui-button [config]="config2"></ui-button>`;
    configTsSnippet2 = `let isDisabled = false;\nconfig2 = { buttonText: "Config 2", disabled: isDisabled };`;

    // Function
    configHtmlSnippet3 = `<ui-button [config]="config3"><ui-button>`;
    configTsSnippet3 = `function getIsDisabled() { return this.isDisabled; }\nconfig3 = {  buttonText: "Config 3", disabled: () => this.isDisabled };`;

    // Signal
    configHtmlSnippet4a = `<ui-button [config]="config4"></ui-button>`;
    configTsSnippet4a = `isDisabledSig = signal(false);\nconfig4 = { buttonText: "Config 4a ✅", disabled: isDisabledSig };`;

    // Signal
    configHtmlSnippet4b = `<ui-button [config]="config4a"></ui-button>`;
    configTsSnippet4b = `isDisabledSig = signal(false);\nconfig4 = { buttonText: "Config 4b ❌", disabled: () => isDisabledSig() };`;

    // Observable
    configTsSnippet5 = `config5 = { buttonText: "Config 5", disabled: toSignal(this.isDisabled$) as Signal<boolean> };`;

    config1: ButtonConfig = {
      id: crypto.randomUUID(),
      buttonText: "Config 1",
      disabled: true
    };

    config2: ButtonConfig = {
      id: crypto.randomUUID(),
      buttonText: "Config 2",
      disabled: this.isDisabled
    };

    config3: ButtonConfig = {
      id: crypto.randomUUID(),
      buttonText: "Config 3",
      disabled: () => this.isDisabled
    };

    config4a: ButtonConfig = {
      id: crypto.randomUUID(),
      buttonText: "Config 4a ✅",
      disabled: this.isDisabledSig
    };

    config4b: ButtonConfig = {
      id: crypto.randomUUID(),
      buttonText: "Config 4b ❌",
      disabled: () => this.isDisabledSig()
    };

      config5: ButtonConfig = {
      id: crypto.randomUUID(),
      buttonText: "Config 5",
      disabled: toSignal(this.isDisabled$) as Signal<boolean>,
    };
}
